'use client';

import { Button, Checkbox, Divider, DropdownMenu } from '@common/components';
import { useToast } from '@common/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { AuthApi } from '@/api';
import { PageNavigation } from '@/components/client';
import { useAuthStore } from '@/stores/AuthStore';

const getDiscountLabel = (coupon: {
  couponType?: string;
  discountType?: string;
  discountValue?: number;
}) => {
  if (coupon.couponType === 'FREE_SHIPPING') return '배송비무료';
  if (coupon.discountType === 'FIXED')
    return `${coupon.discountValue?.toLocaleString()}원`;
  if (coupon.discountType === 'PERCENTAGE') return `${coupon.discountValue}%`;

  return '';
};

const calculateDaysRemaining = (expireAt?: string) => {
  if (!expireAt) return null;
  const endDate = new Date(expireAt);
  const today = new Date();
  const diffTime = endDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

const formatDate = (isoString?: string) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}.${mm}.${dd}`;
};

const renderBadge = (coupon: { status?: string; expireAt?: string }) => {
  if (coupon.status === 'USED') {
    return (
      <span className="rounded-xsmall bg-[#6E7481] px-6 py-2 font-600 text-Gray-white typo-caption2">
        사용 만료
      </span>
    );
  }

  const daysLeft = calculateDaysRemaining(coupon.expireAt);

  if (coupon.status === 'EXPIRED' || (daysLeft !== null && daysLeft <= 0)) {
    return (
      <span className="rounded-xsmall bg-[#6E7481] px-6 py-2 font-600 text-Gray-white typo-caption2">
        기간 만료
      </span>
    );
  }

  if (daysLeft !== null) {
    return (
      <span className="rounded-xsmall bg-[#FF8428] px-6 py-2 font-600 text-Gray-white typo-caption2">
        {daysLeft}일 남음
      </span>
    );
  }

  return null;
};

type CouponFilter = 'ALL' | 'AVAILABLE';
type CouponSort = 'DISCOUNT' | 'RECENT';

const MyProductCouponsPage = () => {
  const { toast } = useToast();
  const myInfo = useAuthStore((state) => state.myInfo);
  const [couponCode, setCouponCode] = useState('');
  const [showAvailableOnly, setShowAvailableOnly] = useState(true);
  const [sort, setSort] = useState<CouponSort>('DISCOUNT');

  const filter: CouponFilter = showAvailableOnly ? 'AVAILABLE' : 'ALL';

  const { data } = useQuery({
    queryKey: ['my-product-coupons', filter, sort],
    queryFn: () =>
      AuthApi['get:/wello/v2/auth/local-market/coupons/my']({
        query: { filter, sort },
      }),
  });

  const coupons = data?.data?.context ?? [];

  const handleRegisterCoupon = () => {
    if (!couponCode.trim()) {
      toast({ message: '쿠폰 코드를 입력해주세요', type: 'fail' });

      return;
    }
    // TODO: 쿠폰 코드 등록 API 연동
    toast({ message: '쿠폰 코드 등록 기능은 준비 중입니다' });
  };

  return (
    <article className="flex flex-col">
      <PageNavigation title="쿠폰함" />

      <h1 className="mb-36 px-20 font-700 text-Gray-900 typo-h5 mobile-tablet:hidden">
        쿠폰함
      </h1>

      {/* 상단 쿠폰 입력 영역 */}
      <div className="flex flex-col gap-12 px-20 pt-20">
        <p className="font-700 text-Gray-900 typo-body1">
          {myInfo?.nickName || '회원'}님을 위한
          <br />
          웰로의 혜택 쿠폰
        </p>
        <div className="flex gap-6">
          <input
            type="text"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            placeholder="쿠폰을 등록해 주세요."
            className="h-48 min-w-0 max-w-[23.4375rem] flex-1 rounded-xsmall border border-Gray-300 px-16 font-400 text-Gray-900 typo-body3 placeholder:text-Gray-400 mobile-tablet:max-w-none"
          />
          <Button
            theme="primary-fill"
            dimensions="H48"
            className="shrink-0 rounded-small"
            onClick={handleRegisterCoupon}
          >
            쿠폰등록
          </Button>
        </div>
      </div>

      <Divider className="mt-32 mobile-tablet:h-10" />

      {/* 상품 쿠폰 리스트 */}
      <div className="flex flex-col gap-20 px-20 pb-28 pt-36 mobile-tablet:gap-12">
        <h2 className="font-700 text-Gray-900 typo-h6 mobile-tablet:typo-body2">
          상품 쿠폰
        </h2>

        {/* 카운터 + 정렬 */}
        <div className="flex items-center justify-between">
          <span className="font-500 text-Gray-black typo-body4">
            전체 {coupons.length}개
          </span>
          <DropdownMenu
            type="text"
            value={sort}
            onChange={(v) => setSort(v as CouponSort)}
            options={[
              { label: '할인순', value: 'DISCOUNT' as const },
              { label: '최신순', value: 'RECENT' as const },
            ]}
            contentClassName="!w-auto !min-w-max"
          />
        </div>

        {/* 사용 가능 필터 */}
        <div className="flex justify-end self-stretch">
          <Checkbox
            checked={showAvailableOnly}
            onCheckedChange={setShowAvailableOnly}
            className="flex-row-reverse [&_button[data-state=checked]]:bg-[#FF8428]"
          >
            <span className="font-400 text-[#6E7481] typo-caption1">
              사용 가능한 쿠폰만 보기
            </span>
          </Checkbox>
        </div>

        {coupons.length > 0 ? (
          <ul className="grid grid-cols-2 gap-12 gap-x-20 mobile-tablet:grid-cols-1">
            {coupons.map((coupon) => (
              <li
                key={coupon.couponIssueId}
                className="rounded-xsmall bg-Gray-200 p-28"
              >
                <div className="flex flex-col gap-4">
                  {/* 금액 + 배지 (양쪽 끝) */}
                  <div className="flex items-center justify-between">
                    <span className="font-700 text-Gray-black typo-h7">
                      {getDiscountLabel(coupon)}
                    </span>
                    {renderBadge(coupon)}
                  </div>
                  {/* 쿠폰 제목 */}
                  <span className="font-600 text-[#535861] typo-caption1">
                    {coupon.couponTitle}
                  </span>
                  {/* 최소 주문금액 */}
                  {coupon.minOrderAmount !== null &&
                    coupon.minOrderAmount !== undefined && (
                      <span className="font-400 text-[#6E7481] typo-caption1">
                        {coupon.minOrderAmount.toLocaleString()}원 이상 구매 시
                        사용 가능
                      </span>
                    )}
                  {/* 만료일 */}
                  {coupon.expireAt && (
                    <span className="font-400 text-[#6E7481] typo-caption1">
                      {formatDate(coupon.expireAt)}까지
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex items-center justify-center py-80">
            <p className="font-400 text-Gray-550 typo-body3">
              보유한 쿠폰이 없습니다
            </p>
          </div>
        )}
      </div>
    </article>
  );
};

export default MyProductCouponsPage;
